import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api/client';
import { useWorkflows } from '../../lib/workflows/useWorkflows';
import { DemographicsGrid, isFieldInvalid } from '../capture/DemographicsFields';

interface Descriptive {
  tcn: string;
  captureType: string;
  fields: Record<string, string>;
  editable: boolean;
  blockingRecordTypes: number[];
  /**
   * True for a REJECTED DOCE submission: editing it exists so the operator can correct the data and
   * send it back to the FBI channel, which is free — the transaction was paid for at first submission.
   */
  resubmittable?: boolean;
}
interface AuditEntry { createdOn: string; userEmail: string | null; action: string; changes: { field: string; from: string; to: string }[] }

const when = (iso: string) => new Date(iso).toLocaleString();

export function EditTransaction() {
  const { tcn = '' } = useParams<{ tcn: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const workflows = useWorkflows();
  const [values, setValues] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [resubmitted, setResubmitted] = useState(false);
  // Red only after an attempt to save, so the form isn't hostile on arrival.
  const [showErrors, setShowErrors] = useState(false);

  const desc = useQuery({
    queryKey: ['tx-descriptive', tcn],
    queryFn: () => api.get<Descriptive>(`/transactions/${encodeURIComponent(tcn)}/descriptive`),
  });
  const audit = useQuery({
    queryKey: ['tx-audit', tcn],
    queryFn: () => api.get<AuditEntry[]>(`/transactions/${encodeURIComponent(tcn)}/audit`),
  });

  useEffect(() => { if (desc.data) setValues(desc.data.fields); }, [desc.data]);

  const wf = workflows.data?.find((w) => w.captureType === desc.data?.captureType);
  // Format failures block the save; see the banner below.
  const invalidFields = (wf?.fields ?? []).filter((f) => isFieldInvalid(f, values[f.name]));

  const save = useMutation({
    mutationFn: () => api.put(`/transactions/${encodeURIComponent(tcn)}/descriptive`, { fields: values }),
    onSuccess: () => {
      setErr(null); setSaved(true);
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['tx-audit', tcn] });
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not save your changes'),
  });

  /**
   * Send the corrected EBTS back to the FBI channel. No body beyond the tcn: the save above already
   * wrote the corrected file and pointed the transaction at it, so there is nothing to upload here.
   */
  const resubmit = useMutation({
    mutationFn: () => api.post('/doce/resubmit', { tcn }),
    onSuccess: () => {
      setErr(null);
      setResubmitted(true);
      qc.invalidateQueries({ queryKey: ['transactions'] });
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not resubmit this transaction'),
  });

  if (desc.isLoading || workflows.isLoading) return <div className="loading-screen">// loading transaction…</div>;
  if (desc.isError) {
    return (
      <div className="panel empty">
        <h3>Can't edit this transaction</h3>
        <p>{desc.error instanceof ApiError ? desc.error.message : 'Unable to load the EFT.'}</p>
        <Link to="/app/transactions" className="track-link">← Back to Transactions</Link>
      </div>
    );
  }

  return (
    <div>
      <div className="console-head">
        <div>
          <h1>Edit transaction</h1>
          <div className="sub"><Link to="/app/transactions" className="track-link">← Transactions</Link> · <span className="mono">{tcn}</span></div>
        </div>
      </div>

      <div className="scan-hint" style={{ marginBottom: 16 }}>
        <span className="scan-hint-ico" aria-hidden>✏️</span>
        <span>You're editing the descriptive data only. Fingerprint images can't be edited — they stay exactly as captured.</span>
      </div>

      {desc.data && !desc.data.editable ? (
        <div className="panel empty">
          <h3>This EFT can't be edited</h3>
          <p>It contains record type(s) {desc.data.blockingRecordTypes.join(', ')} that we can't safely rewrite yet. Please contact support.</p>
        </div>
      ) : !wf ? (
        <div className="panel empty"><h3>Unknown workflow</h3><p>No form is configured for “{desc.data?.captureType}”.</p></div>
      ) : (
        <>
          <div className="panel">
            <DemographicsGrid
              fields={wf.fields}
              specification={wf.specification}
              values={values}
              onChange={(name, v) => { setValues((prev) => ({ ...prev, [name]: v })); setSaved(false); }}
              showErrors={showErrors}
            />
            {err && <p className="cart-error" style={{ marginTop: 12 }}>{err}</p>}
            {saved && !desc.data?.resubmittable && (
              <p className="banner-ok" style={{ marginTop: 12 }}>Saved. The stored EFT has been updated — re-download it from Transactions to get the corrected file.</p>
            )}
            {saved && desc.data?.resubmittable && !resubmitted && (
              <p className="banner-ok" style={{ marginTop: 12 }}>
                Saved. The corrected file is ready — resubmit it to the FBI channel to send it.
              </p>
            )}
            {resubmitted && (
              <p className="banner-ok" style={{ marginTop: 12 }}>
                Resubmitted to the FBI channel. You were not charged again for this transaction.
              </p>
            )}
            {/* This screen exists to FIX a rejected submission, so saving a value the EBTS will reject
                again is the one outcome it must not allow. */}
            {showErrors && invalidFields.length > 0 && (
              <div className="banner-warn" style={{ marginTop: 12 }}>
                Correct these before saving: {invalidFields.map((f) => f.label).join(', ')}.
              </div>
            )}
            <div className="flow-actions">
              <button className="btn btn-ghost" onClick={() => navigate('/app/transactions')}>Cancel</button>
              <button
                className="btn btn-primary"
                disabled={save.isPending || resubmit.isPending}
                onClick={() => {
                  if (invalidFields.length > 0) { setShowErrors(true); return; }
                  save.mutate();
                }}
              >
                {save.isPending ? 'Saving…' : 'Save changes'}
              </button>
              {/* Only after a save: resubmitting before saving would send the file the FBI already rejected. */}
              {desc.data?.resubmittable && saved && !resubmitted && (
                <button className="btn btn-primary" disabled={resubmit.isPending} onClick={() => resubmit.mutate()}>
                  {resubmit.isPending ? 'Resubmitting…' : 'Resubmit to FBI'}
                </button>
              )}
            </div>
          </div>

          {(audit.data?.length ?? 0) > 0 && (
            <>
              <div className="section-title">Edit history</div>
              <div className="panel">
                <ul className="audit-list">
                  {audit.data?.map((a, i) => (
                    <li key={i}>
                      <div className="audit-meta">{when(a.createdOn)} · {a.userEmail ?? 'someone'}</div>
                      <ul className="audit-changes">
                        {a.changes.length === 0 && <li className="muted">No field changes recorded</li>}
                        {a.changes.map((c, j) => (
                          <li key={j}><span className="mono">{c.field}</span>: <span className="muted">{c.from || '—'}</span> → <strong>{c.to || '—'}</strong></li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
